import { ITEMS, count, whetCost, wearing } from './sim/inventory.ts';
import { MAX_HP_CAP } from './rules.ts';
/** What glim a press costs THIS state: the miser charm takes a fifth off, and the plate must quote its own price. */
const glimCost = (state: any, n: number) => Math.max(1, Math.round(n * (wearing(state, 'miser') ? 0.8 : 1)));
/* What the on-screen controls say, and what they are called.
 *
 * These lived inline in `main.js` as two independent things: `textContent`,
 * rewritten every frame from the game state, and `aria-label`, a fixed string
 * in `index.html`. So the button that read `DIVE` was announced as "Place a
 * rattle pod" and the one that read `THROW` as "Swing the sickle" — not missing
 * polish, but wrong information, and the only information a player using a
 * screen reader has. One function returns both, so they cannot drift, and a
 * check can call it without a DOM.
 */

/* WHICH VERB, DECIDED ONCE.
 *
 * `actionLabel` used to take `(tool, state, submerged)` — and once each tool
 * grew a second, context-sensitive verb, that signature could not see any of
 * the context that CHOOSES the verb: whether a pod is already down, whether
 * the lung is full, whether you are standing in water, whether there is a
 * brazier in front of you. So the button named the wrong action in five of
 * eight situations, measured: it read `pod 4` while the press set off the pod
 * already lying there, `dive` on dry land while the press sprayed the
 * lungful, `burn` while standing in water where the press douses the ember.
 * That is exactly the class this file exists to prevent, arriving by a new
 * route — the label was kept in step with the tool and not with the action.
 *
 * So the branch lives here, once, and `Game.useTool` switches on the SAME
 * answer. The button cannot name a verb the press will not run, because the
 * press asks this function which verb it is running.
 */

/** What the equipped tool will do if the button is pressed right now.
 *  @param {string} tool  'pods' | 'tide' | 'ember'
 *  @param {object} ctx   the world facts that select a verb
 *  @returns {string} a verb id */
export function toolVerb(tool, ctx: any = {}) {
  if (tool === 'tide') {
    if (ctx.submerged) return 'rise';
    if (ctx.inDeep) return 'dive';
    if (ctx.lungFull) return 'spray';
    // on dry land with an empty lung, the charm calls the tide out (LS-176);
    // with the ebb already called, or no deep water here, it has nothing to do
    return ctx.canEbb ? 'ebb' : 'idle';
  }
  if (tool === 'ember') {
    if (ctx.inWater) return 'douse';
    if (ctx.emberCool > 0) return 'cooling';
    return ctx.brazierAhead ? 'light' : 'drop';
  }
  return ctx.livePod ? 'setoff' : 'place';
}

/** The action button: whatever the equipped tool will do next. */
export function actionLabel(tool, state: any = {}, ctx = {}) {
  /* THE SELECTED STACK FIRST (LS-229): with sap picked on the hotbar the
   * button eats it (game.ts useTool), so the label must say so -- it read
   * 'pod 4' and ate sap, the exact lie this file was written to end. */
  const sel = state && state.bag && state.bag.slots ? state.bag.slots[state.bag.selected || 0] : null;
  if (sel && ITEMS[sel.kind] && ITEMS[sel.kind].place) return { text: 'set ' + ITEMS[sel.kind].name, aria: 'Set down one ' + ITEMS[sel.kind].name + ' in front of you' };   // the placement verb (LS-379)
  if (sel && ITEMS[sel.kind] && ITEMS[sel.kind].eat) return { text: 'eat ' + ITEMS[sel.kind].name, aria: 'Eat one ' + ITEMS[sel.kind].name + ': two vigor back, a wound closed' };
  /* ...AND IT NAMES THE VERB ONLY IF THE PRESS WOULD TAKE (2026-09-17): the plate read "whet" with an empty purse and no whetstone, and the press refused. */
  if (sel && ITEMS[sel.kind] && ITEMS[sel.kind].weapon && ITEMS[sel.kind].dur && sel.dur !== undefined && sel.dur < ITEMS[sel.kind].dur && (count(state, 'whetstone') > 0 || count(state, 'glim') >= whetCost(state))) return { text: 'whet', aria: 'Whet the ' + ITEMS[sel.kind].name + ' for three glim' };   // a worn weapon picked (LS-238)
  if (sel && ITEMS[sel.kind] && ITEMS[sel.kind].gear) return { text: 'wear', aria: 'Wear the ' + ITEMS[sel.kind].name };   // a piece of gear picked (LS-239)
  /* ...AND GLIM, WHICH IS THE ONLY PERMANENT PROGRESSION THE GAME HAS (the design lens, round 52). With glim
   * picked the button read `pod` and the press turned ten of it into a vigor you keep for the rest of the run --
   * so the one thing that makes a player stronger for good was the one action the button would not name. Sap has
   * said `eat sap` since LS-229 for exactly this reason. It says whether there is enough, because "press" on a
   * purse of four is a promise the press refuses. */
  /* THE PRICE ON THE PLATE IS THE PRICE THE PRESS CHARGES (2026-09-17). Both of these quoted the list price, so a
   * player wearing the miser charm read "press 8/10" while the press itself took eight and succeeded -- and at the
   * vigor cap the plate still said "press" for a press that refuses. */
  if (sel && sel.kind === 'glim' && (ctx as any).brazier && sel.n >= glimCost(state, 5)) return { text: 'pitch', aria: 'Five glim beside the fire is a pot of pitch' };   // the press makes pitch here (game.ts useTool)
  if (sel && sel.kind === 'glim') {
    const price = glimCost(state, 10);
    if (state && state.maxHp >= MAX_HP_CAP) return { text: 'press full', aria: 'Your vigor is as high as it goes: the press has nothing left to buy' };
    return sel.n >= price
      ? { text: 'press', aria: 'Press ' + price + ' glim into one more vigor' }
      : { text: 'press ' + sel.n + '/' + price, aria: price + ' glim press into one more vigor; you have ' + sel.n };
  }
  const verb = toolVerb(tool, ctx);
  switch (verb) {
    case 'rise': return { text: 'rise', aria: 'Rise out of the water' };
    case 'dive': return { text: 'dive', aria: 'Dive under the water' };
    case 'spray': return { text: 'spray', aria: 'Spray the lungful of water' };
    case 'ebb': return { text: 'ebb', aria: 'Call the tide out for a minute' };
    case 'idle': return { text: 'tide', aria: 'The tide charm has nothing to do here' };
    case 'douse': return { text: 'douse', aria: 'Douse the ember in the water' };
    case 'light': return { text: 'light', aria: 'Light the brazier ahead' };
    case 'cooling':
    case 'drop': return { text: 'burn', aria: 'Set down an ember' };
    case 'setoff': return { text: 'set off', aria: 'Set off the rattle pod' };
    default:
      return state.hasPods
        ? { text: 'pod ' + state.pods, aria: 'Place a rattle pod, ' + state.pods + ' left' }
        : { text: 'pod', aria: 'Place a rattle pod' };
  }
}

/** The sickle button: swing, or throw once the beam is charged. */
export function swingLabel(ready, weapon = 'sickle') {
  /* THE BUTTON NAMES THE WEAPON IN HAND (the art and mobile lenses, 2026-09-05): it said 'sickle' and showed a
   * sickle with a spear wielded. Only the sickle throws. */
  if (weapon === 'fist') return { text: 'fist', aria: 'Strike bare-handed' };   // nothing wielded (LS-238)
  if (weapon !== 'sickle') return { text: weapon, aria: 'Strike with the ' + weapon };
  return ready
    ? { text: 'throw', aria: 'Throw the sickle' }
    : { text: 'sickle', aria: 'Swing the sickle' };
}

/** The mute toggle, which also has to say which way it is set. */
export function muteLabel(muted) {
  return muted
    ? { text: 'muted', aria: 'Sound is muted', pressed: 'true' }
    : { text: 'sound', aria: 'Sound is on', pressed: 'false' };
}
